﻿export interface PageMeta
{
    [key: string]: string | number | undefined;

    canonical?: string;
    title?: string;
    keywords?: string;
    description?: string;

    ogUrl?: string;
    ogSiteName?: string;
    ogType?: string;
    ogImage?: string;
    ogImageWidth?: number;
    ogImageHeight?: number;
    ogPriceAmount?: number;
    ogPriceCurrency?: string;
    ogTitle?: string;
    ogDescription?: string;

    twitterTitle?: string;
    twitterDescription?: string;
    twitterCard?: string;
    twitterSite?: string;
    twitterCreator?: string;
    twitterImage?: string;

    imageSrc?: string;
}
export class metaManager
{
    private meta: PageMeta = {};

    constructor(initial?: Partial<PageMeta>)
    {
        if (initial)
        {
            this.update(initial);
        }
    }

    getMeta(): PageMeta
    {
        return this.meta;
    }

    update(incoming: Partial<PageMeta>): void
    {
        for (const key in incoming)
        {
            const value = incoming[key];
            if (value !== undefined && value !== null)
            {
                this.meta[key] = value;
            }
        }
    }

    static parseFromHtml(html: string): PageMeta
    {
        const dom = new DOMParser().parseFromString(html, "text/html");
        const meta: PageMeta = {};

        const readMeta = (selector: string): string | undefined =>
        {
            const el = dom.querySelector(selector);
            return el ? el.getAttribute("content") || undefined : undefined;
        };

        meta.canonical = dom.querySelector('link[rel="canonical"]')?.getAttribute("href") || undefined;
        meta.imageSrc = dom.querySelector('link[rel="image_src"]')?.getAttribute("href") || undefined;

        meta.title = dom.querySelector("title")?.textContent || undefined;
        meta.keywords = readMeta('meta[name="keywords"]');
        meta.description = readMeta('meta[name="description"]');

        meta.ogUrl = readMeta('meta[property="og:url"]');
        meta.ogSiteName = readMeta('meta[property="og:site_name"]');
        meta.ogType = readMeta('meta[property="og:type"]');
        meta.ogImage = readMeta('meta[property="og:image"]');
        meta.ogImageWidth = Number(readMeta('meta[property="og:image:width"]'));
        meta.ogImageHeight = Number(readMeta('meta[property="og:image:height"]'));
        meta.ogPriceAmount = Number(readMeta('meta[property="og:price:amount"]'));
        meta.ogPriceCurrency = readMeta('meta[property="og:price:currency"]');
        meta.ogTitle = readMeta('meta[property="og:title"]');
        meta.ogDescription = readMeta('meta[property="og:description"]');

        meta.twitterTitle = readMeta('meta[name="twitter:title"]');
        meta.twitterDescription = readMeta('meta[name="twitter:description"]');
        meta.twitterCard = readMeta('meta[name="twitter:card"]');
        meta.twitterSite = readMeta('meta[name="twitter:site"]');
        meta.twitterCreator = readMeta('meta[name="twitter:creator"]');
        meta.twitterImage = readMeta('meta[name="twitter:image"]');

        return meta;
    }

    /**
     * Update HTML meta tags based on provided PageMeta
     */
    static updateHtml(html: string | null, pageMeta: Partial<PageMeta>): string | void
    {
        const isBrowserMode = !html && typeof document !== "undefined";

        // Choose source: DOMParser or live document
        const dom = isBrowserMode
            ? document
            : new DOMParser().parseFromString(html!, "text/html");

        const head = dom.querySelector("head");

        const ensureTag = (selector: string, create: () => Element): Element =>
        {
            let el = dom.querySelector(selector);
            if (!el)
            {
                el = create();
                head?.appendChild(el);
            }
            return el;
        };

        // Update <title>
        if (pageMeta.title)
        {
            let titleEl = dom.querySelector("title");
            if (!titleEl)
            {
                titleEl = dom.createElement("title");
                head?.appendChild(titleEl);
            }
            titleEl.textContent = pageMeta.title;
        }

        // Update canonical
        if (pageMeta.canonical)
        {
            const link = ensureTag(
                'link[rel="canonical"]',
                () =>
                {
                    const el = dom.createElement("link");
                    el.setAttribute("rel", "canonical");
                    return el;
                }
            );
            link.setAttribute("href", pageMeta.canonical);
        }

        // Update image_src
        if (pageMeta.imageSrc)
        {
            const link = ensureTag(
                'link[rel="image_src"]',
                () =>
                {
                    const el = dom.createElement("link");
                    el.setAttribute("rel", "image_src");
                    return el;
                }
            );
            link.setAttribute("href", pageMeta.imageSrc);
        }

        const setMeta = (nameOrProp: string, value: string | number | undefined) =>
        {
            if (value === undefined || value === null) return;

            const selector = nameOrProp.startsWith("og:")
                ? `meta[property="${nameOrProp}"]`
                : `meta[name="${nameOrProp}"]`;

            const el = ensureTag(selector, () =>
            {
                const m = dom.createElement("meta");
                if (nameOrProp.startsWith("og:"))
                    m.setAttribute("property", nameOrProp);
                else
                    m.setAttribute("name", nameOrProp);
                return m;
            });

            el.setAttribute("content", String(value));
        };

        // Standard meta
        setMeta("keywords", pageMeta.keywords);
        setMeta("description", pageMeta.description);

        // OpenGraph
        setMeta("og:url", pageMeta.ogUrl);
        setMeta("og:site_name", pageMeta.ogSiteName);
        setMeta("og:type", pageMeta.ogType);
        setMeta("og:image", pageMeta.ogImage);
        setMeta("og:image:width", pageMeta.ogImageWidth);
        setMeta("og:image:height", pageMeta.ogImageHeight);
        setMeta("og:price:amount", pageMeta.ogPriceAmount);
        setMeta("og:price:currency", pageMeta.ogPriceCurrency);
        setMeta("og:title", pageMeta.ogTitle);
        setMeta("og:description", pageMeta.ogDescription);

        // Twitter
        setMeta("twitter:title", pageMeta.twitterTitle);
        setMeta("twitter:description", pageMeta.twitterDescription);
        setMeta("twitter:card", pageMeta.twitterCard);
        setMeta("twitter:site", pageMeta.twitterSite);
        setMeta("twitter:creator", pageMeta.twitterCreator);
        setMeta("twitter:image", pageMeta.twitterImage);

        // Browser mode: we modify the live DOM → no return value
        if (isBrowserMode)
            return;

        // HTML mode: return updated HTML string
        return dom.documentElement.outerHTML;
    }
}
